import { Injectable } from '@angular/core';
import { UtilsService } from '../../shared/utils.service';

@Injectable({
  providedIn: 'root'
})
export class AssetMaintenanceRecordsService {
  readonly TYPES: Record<string, string> = {
    preventive: 'Preventive',
    corrective: 'Corrective',
    predictive: 'Predictive',
    inspection: 'Inspection',
    compliance_safety: 'Compliance & Safety',
    calibration_servicing: 'Calibration & Servicing',
    installation_commissioning: 'Installation & Commissioning',
    upgrade_modification: 'Upgrade & Modification',
    decommissioning: 'Decommissioning',
    other: 'Other'
  };

  readonly PRIORITIES: Record<string, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent'
  };

  readonly STATUSES: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };

  readonly TIME_SPENT_UNITS: Record<string, string> = {
    minutes: 'Minutes',
    hours: 'Hours',
    days: 'Days'
  };

  readonly TRIGGER_TYPES: Record<string, string> = {
    manual: 'Manually Created',
    date: 'Auto Created (Scheduled Due Date)'
  };

  constructor(private utils: UtilsService) {}

  getTypeLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.TYPES, value);
  }

  getPriorityLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.PRIORITIES, value);
  }

  getStatusLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.STATUSES, value);
  }

  getTimeSpentUnitLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.TIME_SPENT_UNITS, value);
  }

  getTriggerTypeLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.TRIGGER_TYPES, value);
  }

  /**
   * Check if an "Other" maintenance type description should be shown.
   */
  isOtherType(value: string | null | undefined): boolean {
    return value === 'other';
  }

  /**
   * Returns a color code based on the maintenance priority.
   *
   * @param {string | null | undefined} value The priority value to determine the color for.
   * @return {string | null} The color code corresponding to the priority value, or null if no corresponding color is found.
   */
  getPriorityColor(value: string | null | undefined): string | null {
    switch (value) {
      case 'low':
        return '#2e7d32';
      case 'medium':
        return '#f9a825';
      case 'high':
        return '#ef6c00';
      case 'urgent':
        return '#c62828';
      default:
        return null;
    }
  }

  /**
   * Returns a color code based on the maintenance record status.
   *
   * @param {string | null | undefined} value The status value to determine the color for.
   * @return {string | null} The color code corresponding to the status value, or null if no corresponding color is found.
   */
  getStatusColor(value: string | null | undefined): string | null {
    switch (value) {
      case 'open':
        return '#1565c0';
      case 'in_progress':
        return '#ef6c00';
      case 'completed':
        return '#2e7d32';
      case 'cancelled':
        return '#c62828';
      default:
        return null;
    }
  }

  /**
   * Returns a formatted time spent value.
   *
   * @param {number | null | undefined} value The time spent value.
   * @param {string | null | undefined} unit The time spent unit.
   * @return {string} The formatted time spent value.
   */
  getTimeSpentLabel(value: number | null | undefined, unit: string | null | undefined): string {
    if (value === null || value === undefined || !unit) {
      return '';
    }

    const singularUnits: Record<string, string> = {
      minutes: 'Minute',
      hours: 'Hour',
      days: 'Day'
    };

    const pluralUnits: Record<string, string> = {
      minutes: 'Minutes',
      hours: 'Hours',
      days: 'Days'
    };

    const units = value === 1
      ? singularUnits
      : pluralUnits;

    return `${value} ${units[unit] ?? unit}`;
  }
}
